/**
 * BrandColorField — one broker brand-colour control (Wave W4 — branding, ui+preview).
 *
 * A label + one-line help, a hex text input (paste #RRGGBB) AND an interactive colour
 * picker in a small popover (react-colorful — ~2KB, zero-dep, injects its own styles
 * so there is no external CSS import). A swatch button opens the popover; picking from
 * the palette or typing a hex both drive the SAME controlled value, so the swatch and
 * the live preview update together.
 *
 * Controlled: the parent owns `value` (a raw hex string, possibly partial/invalid while
 * typing) and gets every change via `onChange`. Invalid input is surfaced inline (the
 * parent decides what to persist — only valid #RRGGBB is ever patched). Tokens only —
 * Brokly palette, no emerald/green (admin brand-guard).
 *
 * [Source: apps/web/src/admin/features/wizard/steps/sections/PortalSection.tsx] (idioms)
 */

import { useEffect, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { isHexColor } from '@brokly/shared';
import { normalizeHex } from './broker-colors';

export function BrandColorField({
  label,
  help,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  help: string;
  /** The current hex string (may be partial/invalid while the operator types). */
  value: string;
  /** The Brokly default shown in the swatch when `value` is empty/invalid. */
  placeholder: string;
  onChange: (hex: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const valid = isHexColor(value);
  // The swatch always shows something: the valid value, else the Brokly default.
  const swatch = valid ? value : placeholder;
  const inputId = `brand-color-${label}`;

  // Close the popover on an outside click or Escape (a lightweight popover).
  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <label htmlFor={inputId} className="block text-sm font-medium text-foreground">
        {label}
      </label>
      <p className="mt-0.5 text-xs text-muted-foreground">{help}</p>

      <div className="mt-2 flex items-center gap-2">
        {/* Swatch — opens the interactive picker popover. */}
        <button
          type="button"
          aria-label={`${label}: Farbwähler öffnen`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="size-9 shrink-0 rounded-md border border-border shadow-xs"
          style={{ backgroundColor: swatch }}
        />
        {/* Hex text input — paste / type #RRGGBB. */}
        <input
          id={inputId}
          type="text"
          inputMode="text"
          spellCheck={false}
          value={value}
          placeholder={placeholder}
          aria-invalid={value !== '' && !valid}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => {
            // Forgiving: normalise on blur (prepend #, lowercase) when it isn't empty.
            if (e.target.value.trim() !== '') onChange(normalizeHex(e.target.value));
          }}
          className="w-32 rounded-md border border-border bg-card px-2 py-1.5 font-mono text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary aria-[invalid=true]:border-destructive"
        />
        {value !== '' && !valid && (
          <span className="text-xs text-destructive">Ungültiger Hex-Wert (#RRGGBB)</span>
        )}
      </div>

      {open && (
        <div className="absolute left-0 z-20 mt-2 rounded-lg border border-border bg-card p-3 shadow-lg">
          <HexColorPicker color={valid ? value : placeholder} onChange={onChange} />
        </div>
      )}
    </div>
  );
}
